import { createAsyncThunk } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/redux/store'
import {
  setConnectionStatus,
  addMessage,
  setError,
  setIsGenerating,
} from '@/redux/slices/gameAnalysisSlice'
import WebSocketManager from '@/services/websocketManager'

const wsManager = WebSocketManager.getInstance()

export const connectGameAnalysisWebSocket = createAsyncThunk<
  void,
  { roomId: string; jwtToken: string },
  { dispatch: AppDispatch; state: RootState }
>('gameAnalysis/connect', async ({ roomId, jwtToken }, { rejectWithValue }) => {
  try {
    await wsManager.connectToGameAnalysisChat(jwtToken, roomId)
  } catch (error) {
    console.error('Failed to connect to game analysis websocket:', error)
    return rejectWithValue(
      (error as Error).message || 'Failed to connect to game analysis websocket'
    )
  }
})

export const sendAnalysisQuery = createAsyncThunk<
  void,
  { message: string; user: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'gameAnalysis/sendQuery',
  async ({ message, user }, { dispatch, rejectWithValue, getState }) => {
    try {
      const state = getState()
      const filters = state.gameAnalysis.selectedFilters

      dispatch(setIsGenerating(true))
      dispatch(
        addMessage({
          id: Date.now(),
          message: message,
          user: user,
          streaming: false,
          filters: filters,
        })
      )

      const success = wsManager.sendMessage('gameAnalysis', {
        type: 'analysis_query',
        message,
        filters,
      })
      if (!success) {
        dispatch(setIsGenerating(false))
        return rejectWithValue('WebSocket is not connected')
      }
    } catch (error) {
      dispatch(setIsGenerating(false))
      return rejectWithValue(
        (error as Error).message || 'Failed to send analysis query'
      )
    }
  }
)

export const stopAnalysisGeneration = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('gameAnalysis/stopGeneration', async (_, { rejectWithValue }) => {
  const success = wsManager.sendMessage('gameAnalysis', {
    type: 'stop_generation',
  })
  if (!success) {
    return rejectWithValue('WebSocket is not connected')
  }
})

export const disconnectGameAnalysisWebSocket = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>('gameAnalysis/disconnect', async (_, { dispatch }) => {
  try {
    await wsManager.disconnect('gameAnalysis')
    dispatch(setConnectionStatus(false))
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Unknown error disconnecting game analysis WebSocket'
    dispatch(setError(errorMessage))
  }
})
